import { call, put, takeLatest, select } from 'redux-saga/effects';
import deviceReportService from '../../../services/deviceReportService';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchDeviceReport,
  fetchDeviceReportSuccess,
  fetchDeviceReportFailure,
  exportDeviceReportToExcel,
  exportDeviceReportToExcelSuccess,
  exportDeviceReportToExcelFailure,
  exportDeviceReportToPdf,
  exportDeviceReportToPdfSuccess,
  exportDeviceReportToPdfFailure,
  refreshDeviceData,
  refreshDeviceDataSuccess,
  refreshDeviceDataFailure,
  selectDeviceReportFilterUnitId,
  selectDeviceReportFilterFromDate,
  selectDeviceReportFilterToDate,
} from './slice';

function* handleFetchInit() {
  try {
    const response = yield call(deviceReportService.getInit);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    yield put(fetchInitFailure(error.message || 'reports.device.errors.fetchInitFailed'));
  }
}

// Helper to check if value is empty (null, undefined, or empty string - NOT 0)
const isEmpty = (value) => value === '' || value === null || value === undefined;

function* handleFetchDeviceReport() {
  try {
    // Get current filter values
    const unitId = yield select(selectDeviceReportFilterUnitId);
    const fromDate = yield select(selectDeviceReportFilterFromDate);
    const toDate = yield select(selectDeviceReportFilterToDate);

    // Validate filters
    if (isEmpty(unitId) || isEmpty(fromDate) || isEmpty(toDate)) {
      yield put(fetchDeviceReportFailure('reports.device.errors.selectFilters'));
      return;
    }

    const params = {
      unitId,
      fromDate,
      toDate,
    };

    const response = yield call(deviceReportService.getDeviceReport, params);
    yield put(fetchDeviceReportSuccess(response));
  } catch (error) {
    yield put(fetchDeviceReportFailure(error.message || 'reports.device.errors.fetchFailed'));
  }
}

function* handleExportDeviceReportToExcel() {
  try {
    // Get current filter values
    const unitId = yield select(selectDeviceReportFilterUnitId);
    const fromDate = yield select(selectDeviceReportFilterFromDate);
    const toDate = yield select(selectDeviceReportFilterToDate);

    // Validate filters (same as fetch report)
    if (isEmpty(unitId) || isEmpty(fromDate) || isEmpty(toDate)) {
      yield put(exportDeviceReportToExcelFailure('reports.device.errors.selectFilters'));
      return;
    }

    const params = {
      unitId,
      fromDate,
      toDate,
    };

    yield call(deviceReportService.exportDeviceReportToExcel, params);
    yield put(exportDeviceReportToExcelSuccess('reports.device.exportSuccess'));
  } catch (error) {
    yield put(exportDeviceReportToExcelFailure(error.message || 'reports.device.errors.exportFailed'));
  }
}

function* handleExportDeviceReportToPdf() {
  try {
    // Get current filter values
    const unitId = yield select(selectDeviceReportFilterUnitId);
    const fromDate = yield select(selectDeviceReportFilterFromDate);
    const toDate = yield select(selectDeviceReportFilterToDate);

    // Validate filters (same as fetch report)
    if (isEmpty(unitId) || isEmpty(fromDate) || isEmpty(toDate)) {
      yield put(exportDeviceReportToPdfFailure('reports.device.errors.selectFilters'));
      return;
    }

    const params = {
      unitId,
      fromDate,
      toDate,
      type: 'pdf',
    };

    yield call(deviceReportService.exportDeviceReportToExcel, params);
    yield put(exportDeviceReportToPdfSuccess('reports.device.exportPdfSuccess'));
  } catch (error) {
    yield put(exportDeviceReportToPdfFailure(error.message || 'reports.device.errors.exportPdfFailed'));
  }
}

function* handleRefreshDeviceData() {
  try {
    // Get current filter values
    const unitId = yield select(selectDeviceReportFilterUnitId);

    // Validate unitId is not 0
    if (isEmpty(unitId) || unitId === 0 || unitId === '0') {
      yield put(refreshDeviceDataFailure('reports.device.errors.selectUnitForRefresh'));
      return;
    }

    yield call(deviceReportService.refreshDeviceData, { unitId });
    yield put(refreshDeviceDataSuccess('reports.device.refreshSuccess'));
  } catch (error) {
    yield put(refreshDeviceDataFailure(error.message || 'reports.device.errors.refreshFailed'));
  }
}

export function* watchDeviceReportActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchDeviceReport.type, handleFetchDeviceReport);
  yield takeLatest(exportDeviceReportToExcel.type, handleExportDeviceReportToExcel);
  yield takeLatest(exportDeviceReportToPdf.type, handleExportDeviceReportToPdf);
  yield takeLatest(refreshDeviceData.type, handleRefreshDeviceData);
}
